/**
 * socketStore.ts
 *
 * Manages the Socket.IO connection for the PitStop web app.
 * Connects with the user's JWT, subscribes to real-time events, and
 * keeps React Query caches in sync so technicians see live device/session
 * state without manual refreshes.
 *
 * Usage (in App.tsx):
 *   const initSocket = useSocketStore(s => s.init)
 *   const destroySocket = useSocketStore(s => s.destroy)
 *   useEffect(() => {
 *     if (token) initSocket(token)
 *     return () => destroySocket()
 *   }, [token])
 */

import { create } from 'zustand'
import { io, Socket } from 'socket.io-client'
import toast from 'react-hot-toast'

// ─── Payload types ────────────────────────────────────────────────────────────

interface DeviceUpdatedPayload {
  deviceId: string
  status: 'online' | 'offline' | 'busy'
  lastSeen: string | null
  rustdeskId?: string | null
  ipAddress?: string | null
  hostname?: string | null
  unattendedEnabled?: boolean
}

interface SessionEventPayload {
  id: string
  deviceName: string
  technicianName?: string
  customerName?: string
  duration?: number
}

// ─── Query cache invalidation callback ───────────────────────────────────────
// Set by the React component layer (App.tsx) so we can invalidate RQ caches
// without importing queryClient here (avoids circular dep).

type InvalidateFn = (keys: string[][]) => void
let _invalidate: InvalidateFn | null = null

export function registerQueryInvalidator(fn: InvalidateFn): void {
  _invalidate = fn
}

function invalidate(...keys: string[][]): void {
  _invalidate?.(keys)
}

// ─── Store ────────────────────────────────────────────────────────────────────

interface SocketState {
  socket: Socket | null
  connected: boolean
  init: (token: string) => void
  destroy: () => void
}

export const useSocketStore = create<SocketState>((set, get) => ({
  socket: null,
  connected: false,

  init(token: string) {
    // Avoid double-connecting
    const existing = get().socket
    if (existing) {
      existing.disconnect()
    }

    const socketUrl = window.location.origin  // proxy in dev, same-origin in prod
    const socket = io(socketUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
    })

    // ── Connection lifecycle ──────────────────────────────────────────────────

    socket.on('connect', () => {
      set({ connected: true })
      console.log('[PitStop] Socket connected')
    })

    socket.on('disconnect', (reason) => {
      set({ connected: false })
      console.log('[PitStop] Socket disconnected:', reason)
    })

    socket.on('connect_error', (err) => {
      console.warn('[PitStop] Socket connect error:', err.message)
    })

    // ── Device events ─────────────────────────────────────────────────────────

    socket.on('device:updated', (_payload: DeviceUpdatedPayload) => {
      // Invalidate device list and dashboard so both auto-refetch
      invalidate(['devices'], ['dashboard-stats'], ['online-devices'])
    })

    // ── Session events ────────────────────────────────────────────────────────

    socket.on('session:started', (payload: SessionEventPayload) => {
      toast.success(`Session started on ${payload.deviceName}`, { id: `session-start-${payload.id}` })
      invalidate(['sessions'], ['active-sessions'], ['recent-sessions'], ['dashboard-stats'])
    })

    socket.on('session:ended', (payload: SessionEventPayload) => {
      const durationMin = payload.duration ? Math.round(payload.duration / 60) : null
      const msg = durationMin != null
        ? `Session ended on ${payload.deviceName} (${durationMin}m)`
        : `Session ended on ${payload.deviceName}`
      toast(msg, { icon: '🏁', id: `session-end-${payload.id}` })
      invalidate(['sessions'], ['active-sessions'], ['recent-sessions'], ['dashboard-stats'], ['devices'])
    })

    set({ socket, connected: false })
  },

  destroy() {
    get().socket?.disconnect()
    set({ socket: null, connected: false })
  },
}))
